import { useEffect, useState } from 'react';
import Showcase from '../showcase';
import { useSearchApi } from './useSearchApi';
import { currentPageKey, recallPageState, rememberPageState } from '../../utils/browse-list-cache';
import { isBackNavigation } from '../../utils/navigation-direction';
import type { NearYouShowcase as NearYouRow } from '../../data/api/search';

/**
 * The home page's local row: listings near where the buyer is, from the
 * listing's geo point and the buyer's IP position (utils/near-you.ts). Fetched
 * on the client like every other showcase. Draws nothing until it has a row,
 * and nothing at all when the server cannot place the buyer.
 *
 * Not `recommendForever`: past the radius the tag-graph fallback would fill the
 * row with items from anywhere under a "Finds Near" heading.
 */
export default function NearYouShowcase() {
  const { fetchNearYouShowcase } = useSearchApi();
  // Back puts back the row this page showed, or its absence, instead of
  // fetching it again and pushing the rows below it down (utils/browse-list-cache.ts).
  const [keepKey] = useState(() => `near-you:${currentPageKey()}`);
  const [kept] = useState(() => (isBackNavigation() ? recallPageState<{ row: NearYouRow | null }>(keepKey) : null));
  const [row, setRow] = useState<NearYouRow | null>(() => kept?.row ?? null);

  useEffect(() => {
    if (kept) return undefined;
    let cancelled = false;
    void fetchNearYouShowcase().then((result) => {
      if (cancelled) return;
      setRow(result);
      rememberPageState(keepKey, { row: result });
    });
    return () => {
      cancelled = true;
    };
  }, [fetchNearYouShowcase, kept, keepKey]);

  if (!row) return null;

  return (
    <Showcase
      title={row.title}
      searchParams={{ near: true }}
      initialListings={row.listings as unknown as Record<string, unknown>[]}
      listName="near-you"
      itemsPerPage={10}
      windowed
    />
  );
}
